import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, ShieldCheck, Cloud, Cpu, Users, Database, ArrowRight, CheckCircle2 } from "lucide-react";
import HeroBackground from "@/components/HeroBackground";
import FinalCTA from "@/components/FinalCTA";
import SectionHeading from "@/components/SectionHeading";
import { COUNTRIES } from "@/data/site";
import { HERO } from "@/constants/testIds";
import { useT } from "@/i18n";

const SERVICE_ICONS = {
  "ai-transformation": Sparkles,
  "dedicated-teams": Users,
  cloud: Cloud,
  cybersecurity: ShieldCheck,
  sap: Database,
  "software-engineering": Cpu,
};

export default function Home() {
  const t = useT();
  return (
    <div>
      <section className="relative overflow-hidden">
        <HeroBackground variant="full" />
        <div className="relative nx-container pt-16 md:pt-24 pb-24 md:pb-36">
          <div className="nx-mono text-xs uppercase tracking-[0.32em] text-[#38D8FF] mb-5 nx-fade-in">
            / {t.hero.eyebrow}
          </div>
          <h1 className="nx-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight max-w-5xl nx-fade-in">
            <span className="nx-gradient-text">{t.hero.title1}</span>
            <br />
            {t.hero.title2}
          </h1>
          <p className="mt-7 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed nx-fade-in">
            {t.hero.subtitle}
          </p>
          <div className="mt-5 flex flex-wrap gap-2 text-xs text-white/55 nx-mono nx-fade-in">
            <span>{t.nav.aiTransformation.toUpperCase()}</span>
            <span className="text-[#38D8FF]">•</span>
            <span>CLOUD</span>
            <span className="text-[#38D8FF]">•</span>
            <span>CYBERSECURITY</span>
            <span className="text-[#38D8FF]">•</span>
            <span>SAP</span>
            <span className="text-[#38D8FF]">•</span>
            <span>SOFTWARE ENGINEERING</span>
          </div>
          <div className="mt-10 flex flex-wrap gap-4 nx-fade-in">
            <Link
              to="/contact"
              data-testid={HERO.primaryCta}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-md bg-[#0057FF] hover:bg-[#2F80FF] text-white text-sm font-medium nx-cta-glow"
            >
              {t.hero.bookDiscovery} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid={HERO.secondaryCta}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-md border border-white/20 hover:border-[#38D8FF] text-white/90 text-sm font-medium transition"
            >
              {t.hero.requestTalent}
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
            {t.trustStats.map((s) => (
              <div key={s.label}>
                <div className="nx-heading text-3xl md:text-4xl font-bold text-white">{s.value}</div>
                <div className="mt-1 text-xs text-white/55 uppercase tracking-[0.18em]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nx-container py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow={t.whyUs.eyebrow} title={t.whyUs.title} lede={t.whyUs.lede} />
          <p className="mt-6 text-white/65 leading-relaxed">{t.whyUs.body1}</p>
          <p className="mt-4 text-white/65 leading-relaxed">{t.whyUs.body2}</p>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 text-sm text-[#38D8FF] hover:text-white transition"
          >
            {t.whyUs.aboutLink} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="lg:col-span-7">
          <div className="nx-card rounded-2xl p-8 md:p-10">
            <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-5 nx-mono">
              / {t.whyUs.deliverablesEyebrow}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.deliverables.map((d) => (
                <div key={d} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-white/8 bg-white/[0.02]">
                  <CheckCircle2 className="w-4 h-4 text-[#38D8FF] shrink-0" />
                  <span className="text-sm text-white/90">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="nx-container py-24 md:py-32">
        <SectionHeading
          eyebrow={t.servicesSection.eyebrow}
          title={t.servicesSection.title}
          lede={t.servicesSection.lede}
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.services.map((s) => {
            const Icon = SERVICE_ICONS[s.slug] || Sparkles;
            return (
              <Link
                key={s.slug}
                to={s.slug === "ai-transformation" ? "/ai-transformation" : "/services"}
                className="nx-card rounded-2xl p-7 group hover:-translate-y-1 transition-transform"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#0057FF]/40 to-[#38D8FF]/20 border border-white/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#38D8FF]" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#38D8FF] transition-colors" />
                </div>
                <h3 className="nx-heading text-xl font-semibold">{s.title}</h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{s.summary}</p>
                <ul className="mt-5 space-y-1.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="text-xs text-white/55 flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#38D8FF]" />
                      {b}
                    </li>
                  ))}
                </ul>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 nx-grid-bg opacity-30" />
        <div className="relative nx-container py-24 md:py-32">
          <SectionHeading
            eyebrow={t.countriesSection.eyebrow}
            title={t.countriesSection.title}
            lede={t.countriesSection.lede}
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {COUNTRIES.map((c) => {
              const key = c.code === "uk" ? "united-kingdom" : c.code === "nl" ? "netherlands" : c.code === "de" ? "germany" : c.code === "ch" ? "switzerland" : "belgium";
              return (
                <Link
                  key={c.code}
                  to={c.path}
                  className="nx-card rounded-2xl p-6 group hover:-translate-y-1 transition-transform"
                >
                  <div className="text-4xl mb-5">{c.flag}</div>
                  <div className="nx-heading text-lg font-semibold">{t.countriesNav[key]}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#38D8FF] group-hover:gap-2 transition-all">
                    {t.countriesSection.exploreLabel} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="nx-container py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/5] md:aspect-[5/6]">
            <img
              src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Abstract AI network nodes"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#071A35]/95 via-[#071A35]/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="nx-mono text-xs uppercase tracking-[0.28em] text-[#38D8FF]">
                / AI / Generative AI / MLOps
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-6">
          <SectionHeading eyebrow={t.aiFeature.eyebrow} title={t.aiFeature.title} lede={t.aiFeature.lede} />
          <div className="mt-8 grid grid-cols-2 gap-3">
            {t.aiFeature.tags.map((l) => (
              <div key={l} className="px-4 py-3 rounded-lg border border-white/10 bg-white/[0.02] text-sm text-white/85">
                {l}
              </div>
            ))}
          </div>
          <Link
            to="/ai-transformation"
            className="mt-8 inline-flex items-center gap-2 text-sm text-[#38D8FF] hover:text-white transition"
          >
            {t.aiFeature.exploreLink} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="nx-container py-24 md:py-32">
        <SectionHeading eyebrow={t.principles.eyebrow} title={t.principles.title} lede={t.principles.lede} />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.principles.items.map((p, i) => (
            <div key={p.title} className="nx-card rounded-2xl p-6">
              <div className="nx-mono text-xs text-[#38D8FF] mb-4">/ 0{i + 1}</div>
              <h3 className="nx-heading text-lg font-semibold">{p.title}</h3>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
